import React, { useState, useEffect, useRef } from 'react';
import { Monitor, SkipForward, Play, Pause } from 'lucide-react';
import { fetchWithCache } from '../sanityClient';
import ReactPlayer from 'react-player';
import SkeletonBlock from './UI/SkeletonBlock';

import { useFrequency } from '../context/FrequencyContext';

const VideoPlayer = ({ isActive = true, user, isMobile = false, onOpenMembers }) => {
    // ... existing state ...
    const { frequency, isTuning } = useFrequency();
    const [visuals, setVisuals] = useState([]);
    const [currentVisualIndex, setCurrentVisualIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(true);
    const [previewLimitReached, setPreviewLimitReached] = useState(false);
    const [videoError, setVideoError] = useState(false);
    const [loading, setLoading] = useState(true);
    const playerRef = useRef(null);

    // Reset limit and error when visual changes
    useEffect(() => {
        setPreviewLimitReached(false);
        setVideoError(false);
        setIsPlaying(true);
    }, [currentVisualIndex, frequency]);

    // Fetch visuals from Sanity (and respect frequency override)
    useEffect(() => {
        const fetchVisuals = async () => {
            try {
                // Fetch global videos AND creator-specific videos
                // Added _id to ensure uniqueness and debugging
                const query = `[
                    ...*[_type == "video"],
                    ...*[_type == "creator" && defined(channelVideo) && defined(channelNumber)]
                ] | order(channelNumber asc) {
                    _id,
                    "src": videoFile.asset->url,
                    "streamUid": coalesce(cloudflareStream.uid, channelVideo.uid),
                    youtubeUrl,
                    channelNumber,
                    restrictionTier
                }`;
                const data = await fetchWithCache(query);

                console.log('[VideoPlayer] Raw data from Sanity:', data);

                if (data && data.length > 0) {
                    let items = data.map(item => {
                        console.log('[VideoPlayer] Processing item:', item);
                        if (item.streamUid) return { id: item._id, type: 'stream', url: item.streamUid, channel: item.channelNumber, restrictionTier: item.restrictionTier };
                        if (item.src) return { id: item._id, type: 'file', url: item.src, channel: item.channelNumber, restrictionTier: item.restrictionTier };
                        if (item.youtubeUrl) return { id: item._id, type: 'youtube', url: item.youtubeUrl, channel: item.channelNumber, restrictionTier: item.restrictionTier };
                        return null;
                    }).filter(Boolean);

                    console.log('[VideoPlayer] Processed items:', items);

                    // Filter by Channel Number if tuned
                    if (frequency.channelNumber) {
                        const channelItems = items.filter(item => item.channel === frequency.channelNumber);
                        console.log(`[VideoPlayer] Tuned to Channel ${frequency.channelNumber}. Found ${channelItems.length} videos.`, channelItems);
                        // Only filter if we actually found videos for this channel
                        if (channelItems.length > 0) {
                            items = channelItems;
                        }
                    }

                    setVisuals(items);
                    if (items.length > 0) {
                        // Ensure we don't get stuck on an index out of bounds if list shrinks
                        setCurrentVisualIndex(prev => prev < items.length ? prev : 0);
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchVisuals();
    }, [frequency.channelNumber]); // Rerun when channel changes

    const nextVisual = () => {
        setCurrentVisualIndex((prev) => (prev + 1) % visuals.length);
    };

    const currentVisual = visuals[currentVisualIndex];

    const isMembersOnly = currentVisual?.restrictionTier === 'members';
    const isUserMissingAccess = !user || !user.isSubscribed || user.membershipTier === 'access';
    const showPaywall = isMembersOnly && isUserMissingAccess;

    const handleProgress = ({ playedSeconds }) => {
        // Enforce Limits
        // Access Tier or Guest: 30s Limit
        const isRestricted = !user || !user.isSubscribed || user.membershipTier === 'access';

        console.log('[VideoPlayer] Progress:', playedSeconds, 'User:', user, 'Restricted:', isRestricted);

        if (isRestricted && playedSeconds > 30 && !previewLimitReached) {
            setPreviewLimitReached(true);
            setIsPlaying(false);
            // Optionally force seek back or just stop
        }
    };

    // Calculate Knob Rotation based on frequency ID
    const getKnobRotation = (id) => {
        if (id === 'root') return 0;
        // Simple hash to stable rotation
        const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return (hash % 270) - 135; // Range -135 to 135
    };

    if (isMobile) {
        return (
            <div className="w-full h-full flex flex-col bg-black">
                {/* Visual Area */}
                <div className="flex-1 relative min-h-0 bg-zinc-900">
                    {!currentVisual && loading && (
                        <SkeletonBlock className="absolute inset-0 rounded-none bg-zinc-800" />
                    )}
                    {currentVisual && !showPaywall && (
                        <div className="w-full h-full">
                            <style>{`video { object-fit: cover !important; }`}</style>
                            <ReactPlayer
                                key={currentVisual.url + currentVisualIndex}
                                ref={playerRef}
                                url={currentVisual.type === 'stream' ? `https://videodelivery.net/${currentVisual.url}/manifest/video.m3u8` : currentVisual.url}
                                playing={isActive && isPlaying && !previewLimitReached && !showPaywall}
                                onEnded={nextVisual}
                                onProgress={handleProgress}
                                onError={(e) => {
                                    console.error('[VideoPlayer] Playback error:', e);
                                    setVideoError(true);
                                }}
                                muted={true}
                                volume={0}
                                playsinline={true}
                                width="100%"
                                height="100%"
                                style={{ opacity: 1, objectFit: 'cover' }}
                                controls={false}
                            />
                        </div>
                    )}

                    {showPaywall && (
                        <div className="absolute inset-0 z-[95] bg-black/90 flex flex-col items-center justify-center font-mono p-6">
                            <div className="text-center">
                                <div className="text-[10px] text-[#AF2E1B] font-bold uppercase tracking-[0.3em] mb-4 flex items-center justify-center gap-2">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                    Members Only
                                </div>
                                <div className="text-[9px] text-zinc-400 uppercase tracking-widest mb-6 max-w-xs mx-auto">This channel contains premium content. Subscribe to unlock full access.</div>
                                {onOpenMembers && (
                                    <button
                                        onClick={onOpenMembers}
                                        className="px-6 py-2.5 bg-[#AF2E1B] text-white text-[9px] font-bold uppercase tracking-[0.2em] hover:bg-[#c0392b] transition-colors rounded-sm"
                                    >
                                        Unlock Channel
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {videoError && (
                        <div className="absolute inset-0 z-[90] bg-black text-white flex items-center justify-center font-mono">
                            <div className="text-center">
                                <div className="text-xs opacity-70 mb-2">VIDEO PROCESSING</div>
                                <div className="text-[10px] opacity-50">Please wait or skip to next</div>
                            </div>
                        </div>
                    )}

                    {isTuning && (
                        <div className="absolute inset-0 z-[100] bg-black text-white flex items-center justify-center font-mono">
                            <div className="animate-pulse tracking-widest text-xs opacity-50">TUNING...</div>
                        </div>
                    )}
                </div>

                {/* Simple Controls */}
                <div className="h-16 flex-none flex justify-between items-center px-6 bg-[#0a0a0a] text-zinc-500 border-t border-white/5">
                    <div className="flex items-center space-x-3 text-xs font-mono">
                        <Monitor size={14} className={isTuning ? "animate-pulse text-red-500" : "text-emerald-500"} />
                        <span className="uppercase tracking-widest font-medium">
                            {isTuning ? 'SCANNING' : (frequency.channelNumber ? `CH ${frequency.channelNumber}` : 'AUX')}
                        </span>
                    </div>

                    <div className="flex space-x-6 items-center">
                        <button
                            onClick={() => setIsPlaying(!isPlaying)}
                            className="text-zinc-400 hover:text-white transition-colors active:scale-95"
                        >
                            {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                        </button>
                        <button
                            onClick={nextVisual}
                            disabled={frequency.id !== 'root'}
                            className={`text-zinc-400 hover:text-white transition-colors active:scale-95 ${frequency.id !== 'root' ? 'opacity-30' : ''}`}
                        >
                            <SkipForward size={18} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col font-sans select-none bg-[#e2e2dd] p-6 rounded-2xl shadow-2xl relative overflow-hidden text-[#333]">

            {/* --- CRT SCREEN AREA --- */}
            <div className="relative w-full aspect-[4/3] bg-[#111] rounded-[2rem] shadow-[inset_0_0_20px_rgba(0,0,0,1)] ring-8 ring-[#222] ring-opacity-90 overflow-hidden mb-6 z-10 border-4 border-[#333]">

                {/* Screen Content Wrapper with border radius matching CRT */}
                <div className="absolute inset-0 rounded-[1.8rem] overflow-hidden bg-black">
                    {/* Skeleton while loading */}
                    {!currentVisual && loading && (
                        <SkeletonBlock className="absolute inset-0 rounded-[1.8rem] bg-zinc-800" />
                    )}
                    {/* React Player */}
                    {currentVisual && !showPaywall && (
                        <div className="w-full h-full p-[2px]"> {/* Tiny inset for bezel gap */}
                            <style>{`video { object-fit: cover !important; border-radius: 1.5rem; }`}</style>
                            <ReactPlayer
                                key={currentVisual.url + currentVisualIndex}
                                ref={playerRef}
                                url={currentVisual.type === 'stream' ? `https://videodelivery.net/${currentVisual.url}/manifest/video.m3u8` : currentVisual.url}
                                playing={isActive && isPlaying && !previewLimitReached && !showPaywall}
                                onEnded={nextVisual}
                                onProgress={handleProgress}
                                onError={(e) => {
                                    console.error('[VideoPlayer] Playback error:', e);
                                    setVideoError(true);
                                }}
                                muted={true}
                                volume={0}
                                playsinline={true}
                                width="100%"
                                height="100%"
                                style={{ opacity: 1, borderRadius: '1.5rem', overflow: 'hidden' }}
                                controls={false}
                            />
                        </div>
                    )}

                    {/* Paywall Overlay */}
                    {showPaywall && (
                        <div className="absolute inset-0 z-[95] bg-black/85 flex flex-col items-center justify-center font-mono rounded-[1.8rem]">
                            <div className="text-center px-6">
                                <div className="text-[10px] text-[#AF2E1B] font-bold uppercase tracking-[0.3em] mb-4 flex items-center justify-center gap-2">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                                    Members Only
                                </div>
                                <div className="w-8 h-px bg-[#AF2E1B]/40 mx-auto mb-4"></div>
                                <div className="text-[8px] text-[#888] uppercase tracking-widest mb-6">Premium content · subscribe for full access</div>
                                {onOpenMembers && (
                                    <button
                                        onClick={onOpenMembers}
                                        className="px-5 py-2 bg-[#AF2E1B] text-white text-[8px] font-bold uppercase tracking-widest hover:bg-[#c0392b] transition-colors rounded-sm shadow-[0_0_10px_rgba(175,46,27,0.4)]"
                                    >
                                        Unlock Area
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Video Error / Processing Overlay */}
                    {videoError && (
                        <div className="absolute inset-0 z-[90] bg-black text-white flex items-center justify-center font-mono rounded-[1.8rem]">
                            <div className="text-center">
                                <div className="text-xs opacity-70 mb-2">VIDEO PROCESSING</div>
                                <div className="text-[10px] opacity-50">Please wait or skip to next</div>
                            </div>
                        </div>
                    )}

                    {/* Preview Limit Overlay */}
                    {previewLimitReached && !videoError && (
                        <div className="absolute inset-0 z-[95] bg-black/85 flex flex-col items-center justify-center font-mono rounded-[1.8rem]">
                            <div className="text-center px-6">
                                <div className="text-[9px] text-[#AF2E1B] font-bold uppercase tracking-[0.3em] mb-3">Preview Ended</div>
                                <div className="w-8 h-px bg-[#AF2E1B]/40 mx-auto mb-3"></div>
                                <div className="text-[8px] text-[#888] uppercase tracking-widest mb-5">30s preview · subscribe for full access</div>
                                {onOpenMembers && (
                                    <button
                                        onClick={onOpenMembers}
                                        className="px-4 py-1.5 bg-[#AF2E1B] text-white text-[8px] font-bold uppercase tracking-widest hover:bg-[#c0392b] transition-colors rounded-sm"
                                    >
                                        Unlock
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Static / Limit / Overlays using existing logic, but ensuring rounded corners */}
                    {isTuning && (
                        <div className="absolute inset-0 z-[100] bg-black text-white flex items-center justify-center font-mono">
                            <div className="animate-pulse tracking-widest text-xs opacity-50">TUNING...</div>
                            {/* ... (Keep existing static noise div if desired, simpler version here for cleanliness) ... */}
                        </div>
                    )}

                    {/* CRT Effects */}
                    <div className="absolute inset-0 pointer-events-none rounded-[1.8rem] z-[60] shadow-[inset_0_0_60px_rgba(0,0,0,0.6)]"></div>
                    {/* Scanlines */}
                    <div className="absolute inset-0 pointer-events-none z-[50] opacity-10 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] bg-[length:100%_4px,6px_100%] rounded-[1.8rem]"></div>
                    {/* Glass Glare */}
                    <div className="absolute inset-0 pointer-events-none z-[70] bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-[1.8rem]"></div>
                </div>
            </div>

            {/* --- BOTTOM CONTROL PANEL --- */}
            <div className="flex h-24 items-end justify-between px-2 pb-2">

                {/* Speaker Grille (Left) */}
                <div className="flex gap-[3px] h-full items-end opacity-60">
                    {[...Array(24)].map((_, i) => (
                        <div key={i} className="w-[3px] h-12 bg-[#333] rounded-full shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)] last:hidden md:last:block"></div>
                    ))}
                </div>

                {/* Right Controls */}
                <div className="flex flex-col items-end gap-3 mb-1">

                    {/* Channel / Status Indication */}
                    <div className="flex items-center gap-3 pr-2">
                        <div className="text-[9px] font-bold tracking-widest text-[#666] uppercase">
                            {isTuning ? 'Scanning' : (frequency.channelNumber ? `CH ${frequency.channelNumber}` : 'AUX')}
                        </div>
                        <div className={`w-2 h-2 rounded-full shadow-inner ${isPlaying ? 'bg-green-500 shadow-[0_0_5px_lime]' : 'bg-red-500'}`}></div>
                    </div>

                    {/* Physical Controls Row */}
                    <div className="flex items-center gap-4 bg-[#d0d0cc] p-1.5 rounded-full shadow-[inset_0_1px_4px_rgba(0,0,0,0.1),0_1px_0_rgba(255,255,255,0.5)] border border-[#ccc]">

                        {/* Tuning Knob (Visual Only or functioning) */}
                        <div className="relative w-8 h-8 rounded-full bg-[#e8e8e5] shadow-[0_2px_4px_rgba(0,0,0,0.2),inset_0_1px_4px_rgba(255,255,255,0.9)] border border-[#bbb] flex items-center justify-center group" title="Tuning">
                            <div className="w-1 h-3 bg-[#444] rounded-full absolute top-1 transition-transform duration-700" style={{ transformOrigin: '50% 12px', transform: `rotate(${getKnobRotation(frequency.id)}deg)` }}></div>
                        </div>

                        {/* Separator */}
                        <div className="w-px h-6 bg-[#bbb]"></div>

                        {/* Play/Pause Button */}
                        <button
                            onClick={() => setIsPlaying(!isPlaying)}
                            className="w-8 h-8 rounded-full bg-[#3a3a3a] shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_2px_rgba(255,255,255,0.1)] flex items-center justify-center active:scale-95 transition-all text-[#ddd] hover:text-white"
                        >
                            {isPlaying ? <Pause size={12} fill="currentColor" /> : <Play size={12} fill="currentColor" className="ml-0.5" />}
                        </button>

                        {/* Next Channel Button */}
                        <button
                            onClick={nextVisual}
                            disabled={frequency.id !== 'root'}
                            className={`w-8 h-8 rounded-full bg-[#b0b0aa] shadow-[0_2px_3px_rgba(0,0,0,0.15),inset_0_1px_2px_rgba(255,255,255,0.4)] flex items-center justify-center active:scale-95 transition-all text-[#444] hover:bg-[#a0a09a] ${frequency.id !== 'root' && 'opacity-50 cursor-not-allowed'}`}
                        >
                            <SkipForward size={14} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Branding */}
            <div className="absolute bottom-4 right-5 text-right select-none pointer-events-none">
                <div className="text-[7px] font-bold tracking-[0.3em] uppercase text-[#aaa] opacity-60">Paradise</div>
                <div className="text-[5px] font-mono tracking-widest text-[#bbb] opacity-40 uppercase mt-0.5">Model PV-01 · Stereo</div>
            </div>

        </div>
    );
};

export default VideoPlayer;
